/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import {
	BackgroundContainer,
	DownloadFooterCard,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
} from "@components/elements";
import { removeStartParam, useDownloadLauncher, useIsBrowser } from "@components/hooks";
import { VStack } from "@panda/jsx";

import { DownloadSteps } from "../download-cards/DownloadSteps.organism";

import { DownloadableCards } from "./DownloadableCards.organism";
import { getDownloadBtnFromSteps } from "./downloadable.utils";
import type { DownloadableSectionFragment } from "@cosmos/hygraph-schema";

export const DownloadableSection = ({
	config,
	instructions,
	executables,
	footer,
}: DownloadableSectionFragment): React.JSX.Element | null => {
	const searchParams = useSearchParams();
	const [windowsUrl, setWindowsUrl] = useState("");
	const [macUrl, setMacUrl] = useState("");
	const steps = useMemo(() => {
		return instructions?.section?.__typename === "CosmosSectionDownloadCard" ? instructions?.section?.steps : [];
	}, [instructions]);

	useEffect(() => {
		const downloadBtnContent = getDownloadBtnFromSteps(steps);
		if (downloadBtnContent) {
			if (downloadBtnContent?.windowsUrl) setWindowsUrl(downloadBtnContent?.windowsUrl);
			if (downloadBtnContent?.macUrl) setMacUrl(downloadBtnContent?.macUrl);
		}
	}, [steps]);

	const noDownloadValue = !windowsUrl && !macUrl;
	const downloadLauncher = useDownloadLauncher(windowsUrl, macUrl);
	const isBrowser = useIsBrowser();

	// Since DownloadableSection will only be used on /download, start the download if ?start=true
	useEffect(() => {
		if (!isBrowser) return;
		if (noDownloadValue) return;
		const noStartParam = !searchParams?.has("start");
		if (noStartParam) return;

		const downloadRun = downloadLauncher();
		if (downloadRun === undefined) return;
		removeStartParam(searchParams.toString());
	}, [downloadLauncher, searchParams, noDownloadValue, isBrowser]);
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(
		steps?.length || executables?.length || footer?.title || footer?.titleIcon || footer?.description || footer?.cta,
	);
	if (!validHeading && !validContent) return null;

	return (
		<BackgroundContainer {...extractBackgroundContainerProps(config)}>
			<ResponsiveContainer>
				<SectionContainer {...extractSectionContainerProps(config)} disablePaddingBlockStart={!steps?.length}>
					<VStack gap="eds.dimension.48" alignItems="stretch" className="downloadable-section">
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<VStack gap="eds.dimension.64" alignItems="stretch">
								<VStack gap="eds.dimension.16" alignItems="stretch">
									{steps?.length ? <DownloadSteps steps={steps || []} /> : null}
									{footer ? <DownloadFooterCard card={footer} /> : null}
								</VStack>
								{executables?.length ? <DownloadableCards cards={executables || []} /> : null}
							</VStack>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
